import React from 'react';
import { gettext } from '@eventbrite/i18n';

import { ScreenSmall } from '@eventbrite/eds-iconography';
import { Lock } from '@eventbrite/eds-iconography';
import { Megaphone } from '@eventbrite/eds-iconography';
import { Eye } from '@eventbrite/eds-iconography';
import { CalendarRepeat } from '@eventbrite/eds-iconography';
import { Stopwatch } from '@eventbrite/eds-iconography';
import { CreditCardBack } from '@eventbrite/eds-iconography';
import { Ticket } from '@eventbrite/eds-iconography';
import { ScanCheck } from '@eventbrite/eds-iconography';
import { Receipt } from '@eventbrite/eds-iconography';
import { Friends } from '@eventbrite/eds-iconography';
import { Money } from '@eventbrite/eds-iconography';
import { LineChart } from '@eventbrite/eds-iconography';
import { Trophy } from '@eventbrite/eds-iconography';
import { Document } from '@eventbrite/eds-iconography';
import { Globe } from '@eventbrite/eds-iconography';
import { ScreenLarge } from '@eventbrite/eds-iconography';
import { Cart } from '@eventbrite/eds-iconography';
import { Row } from '@eventbrite/eds-iconography';
import { Star } from '@eventbrite/eds-iconography';
import { Wifi } from '@eventbrite/eds-iconography';
import { LifePreserver } from '@eventbrite/eds-iconography';
import { Headphone } from '@eventbrite/eds-iconography';
import { Book } from '@eventbrite/eds-iconography';
import { Briefcase } from '@eventbrite/eds-iconography';
import { Gear } from '@eventbrite/eds-iconography';
import { User } from '@eventbrite/eds-iconography';
import { Photo } from '@eventbrite/eds-iconography';
import { CardReader } from '@eventbrite/eds-iconography';
import { Download } from '@eventbrite/eds-iconography';
import { Chat } from '@eventbrite/eds-iconography';

export const MOBILE_OPTIMIZED = {
    heading: gettext('Mobile-optimized event page'),
    description: gettext(
        'Easily create a professional-looking, 100% mobile-optimize event page that looks great on any device.',
    ),
    iconType: <ScreenSmall />,
};

export const SECURE_PAYMENTS = {
    heading: gettext('Secure payments'),
    description: gettext(
        "Accept credit and debit cards without a merchant account using Eventbrite's PCI-compliant payment processor, and pass your fees to attendees.",
    ),
    iconType: <Lock />,
};

export const PROMOTIONAL_TOOLS = {
    heading: gettext('Free promotional tools'),
    description: gettext(
        'Promote your event with free tools like custom email invitations, integrated Facebook promotion, and built-in social sharing.',
    ),
    iconType: <Megaphone />,
};

export const SALES_TRACKING = {
    heading: gettext('Sales at your fingertips'),
    description: gettext(
        "Track sales, counts, and basic attendee info in real-time from any device, so you always know who's coming.",
    ),
    iconType: <Eye />,
};

export const MULTIPLE_EVENTS = {
    heading: gettext('Many events in one'),
    description: gettext(
        'Sell tickets to a series of recurring events — like classes or workshops — on a single event page, so you can efficiently promote and manage your events.',
    ),
    iconType: <CalendarRepeat />,
};

export const INTEGRATIONS_A = {
    heading: gettext('Time-saving integrations'),
    description: gettext(
        "Save time with our seamless integrations with over 170+ tools you already use, like Facebook, Mailchimp, and Raiser's Edge.",
    ),
    iconType: <Stopwatch />,
};

export const INTEGRATIONS_B = {
    heading: gettext('Time-saving integrations'),
    description: gettext(
        'Eliminate manual tasks by seamlessly integrating Eventbrite with 170+ tools you already use, like Salesforce, Mailchimp, and SurveyMonkey.',
    ),
    iconType: <Stopwatch />,
};

export const INTEGRATIONS_C = {
    heading: gettext('Save time with integrations'),
    description: gettext(
        "Eliminate manual tasks by seamlessly integrating Eventbrite with online and on-site tools like Salesforce, Raiser's Edge, and Marcato — or create your own with our open API.",
    ),
    iconType: <Stopwatch />,
};

export const SIMPLE_CHECKOUT = {
    heading: gettext('Simple, secure checkout'),
    description: gettext(
        'Delight your guests with a simple mobile-friendly purchase flow they already trust — no user account, captcha, or login required.',
    ),
    iconType: <CreditCardBack />,
};

export const DIGITAL_TICKETS = {
    heading: gettext('Digital tickets'),
    description: gettext(
        'Let attendees access their tickets from the Eventbrite app, Passbook, Apple Watch, or their email.',
    ),
    iconType: <Ticket />,
};

export const ONE_TICKET_TYPE = {
    heading: gettext('One ticket type'),
    description: gettext(
        'Sell a single type of ticket (for example, "General Admission") at a single price point, for all your attendees.',
    ),
    iconType: <Ticket />,
};

export const FAST_CHECKIN = {
    heading: gettext('Rapid check-in'),
    description: gettext(
        'Use the Eventbrite Organizer app to impress attendees with rapid check-in and ticket sales at the door.',
    ),
    iconType: <ScanCheck />,
};

export const BASIC_TICKETING = {
    heading: gettext('Basic ticketing and registration'),
    description: gettext(
        'Create a professional-looking, 100% mobile-optimized event page to collect payment and basic attendee info.',
    ),
    iconType: <Receipt />,
};

export const SEAMLESS_CHECKIN = {
    heading: gettext('Seamless check-in'),
    description: gettext(
        'Get your attendees in quickly with seamless check-in or at-the-door sales from any device, using our Organizer app',
    ),
    iconType: <ScanCheck />,
};

export const MARKETPLACE = {
    heading: gettext('Reach new audiences'),
    description: gettext(
        'Reach a wider audience and drive sales through our partner sites like Facebook and Spotify.',
    ),
    iconType: <Friends />,
};

export const HIGH_CONVERSION = {
    heading: gettext('4X the conversion'),
    description: gettext(
        'Allow event-goers to purchase tickets wherever they discover your event — converting interest into ticket sales at 4X the average e-commerce rate.',
    ),
    iconType: <Money />,
};

export const CUSTOM_TICKET_OPTIONS = {
    heading: gettext('Customizable ticket options'),
    description: gettext(
        'Create multiple tickets at different prices to attract a wider variety of attendees',
    ),
    iconType: <Ticket />,
};

export const REPORTING_ESSENTIALS = {
    heading: gettext('Reporting essentials'),
    description: gettext(
        'Easily view total sales and RSVPs from any device, and export your detailed order and attendee data.',
    ),
    iconType: <LineChart />,
};

export const DETAILED_ANALYTICS_A = {
    heading: gettext('Detailed analytics'),
    description: gettext(
        'Optimize your marketing with customizable real-time data views on mobile and desktop.',
    ),
    iconType: <LineChart />,
};

export const DETAILED_ANALYTICS_B = {
    heading: gettext('Hone your marketing'),
    description: gettext(
        'Take control of your ticket sales with 24/7 access to real-time data and custom analytics from any device.',
    ),
    iconType: <Trophy />,
};

export const DETAILED_ANALYTICS_C = {
    heading: gettext('Detailed sales analytics'),
    description: gettext(
        'Easily generate actionable insights by ticket type and across multiple events, so you can optimize your marketing.',
    ),
    iconType: <LineChart />,
};

export const MARKETING_TOOLS = {
    heading: gettext('Powerful marketing tools'),
    description: gettext(
        'Extend your marketing with targeted event promotion tools like email marketing, social promotion, and industry-leading SEO.',
    ),
    iconType: <Megaphone />,
};

export const PAYOUTS_A = {
    heading: gettext('Payouts on your schedule'),
    description: gettext(
        'Get paid fast to keep your business liquid, with scheduled payouts before your event.',
    ),
    iconType: <Money />,
};

export const PAYOUTS_B = {
    heading: gettext('Payouts before your event'),
    description: gettext(
        'Apply to get paid weekly or biweekly so you can pay vendors and keep your business liquid.',
    ),
    iconType: <Money />,
    paypalExcl: true,
};

export const PAYOUTS_C = {
    heading: gettext('Get paid on your schedule'),
    description: gettext(
        'Get paid before your event to improve your cash flows and keep vendors in good standing.',
    ),
    iconType: <Money />,
};

export const INSTALLMENT_PAYMENTS = {
    heading: gettext('Installment payments'),
    description: gettext(
        'Increase sales of high-priced tickets by letting buyers pay in monthly installments, at no risk to you.',
    ),
    iconType: <Money />,
};

export const CUSTOM_REGISTRATION = {
    heading: gettext('Customizable registration forms'),
    description: gettext(
        'Learn what you need to about your attendees when they purchase — and avoid surprises on event day.',
    ),
    iconType: <Document />,
};

export const DISTRIBUTION_A = {
    heading: gettext('Reach new audiences'),
    description: gettext(
        'Reach a wider audience and drive sales through our partner sites like Facebook and Spotify.',
    ),
    iconType: <Globe />,
};

export const DISTRIBUTION_B = {
    heading: gettext('Sell where your fans are'),
    description: gettext(
        'Let buyers purchase tickets wherever they discover your event — from your website to Eventbrite to Facebook.',
    ),
    iconType: <Globe />,
};

export const PARTNER_SITES = {
    heading: gettext('Listing on Eventbrite and partner sites'),
    description: gettext(
        'Reach a wider audience and drive sales through our partner sites like Facebook and Spotify.',
    ),
    iconType: <ScreenLarge />,
};

export const CHECKOUT_WIDGET_A = {
    heading: gettext('Sales on your site'),
    description: gettext(
        'Convert hard-earned traffic into sales with our website integrations that make it easy to sell wherever your fans are.',
    ),
    iconType: <Cart />,
};

export const CHECKOUT_WIDGET_B = {
    heading: gettext('Ticket sales on your own site'),
    description: gettext(
        'Convert more interest into ticket buyers by allowing attendees to purchase from your website.',
    ),
    iconType: <Cart />,
    paypalExcl: true,
};

export const CHECKOUT_WIDGET_C = {
    heading: gettext('Make purchases easy'),
    description: gettext(
        'Avoid purchase frustrations with our streamlined, mobile-friendly flow your attendees already know and trust — no user account, captcha, or login required.',
    ),
    iconType: <CreditCardBack />,
};

export const CHECKOUT_WIDGET_D = {
    heading: gettext('Simple, secure checkout on Eventbrite or your website'),
    description: gettext(
        'Avoid purchase frustrations with our streamlined, mobile-friendly flow your attendees already know and trust — no user account, captcha, or login required.',
    ),
    iconType: <CreditCardBack />,
};

export const RESERVED_SEATING = {
    heading: gettext('Reserved seating'),
    description: gettext(
        'Maximize your revenue by creating a map of your venue and charging higher prices for better seats.',
    ),
    iconType: <Row />,
    paypalExcl: true,
};

export const UNLIMITED_TICKET_TYPES = {
    heading: gettext('Unlimited ticket types'),
    description: gettext(
        'Create multiple tickets at different prices and attract a wider variety of attendees.',
    ),
    iconType: <Ticket />,
};

export const VIP_EXPERIENCES_A = {
    heading: gettext('Memorable experiences'),
    description: gettext(
        'Create VIP experiences for your most valued guests with custom registration forms, diverse ticket options, reserved seating, and mobile guest lists.',
    ),
    iconType: <Star />,
};

export const VIP_EXPERIENCES_B = {
    heading: gettext('Delight attendees onsite'),
    description: gettext(
        'Shorten lines and create VIP experience with Organizer app mobile box office and reserved seating',
    ),
    iconType: <Star />,
};

export const ORGANIZER_MOBILE_APP = {
    heading: gettext('Organizer mobile app'),
    description: gettext(
        'Track ticket sales, scan tickets and check-in guests from any iOS or Android device.',
    ),
    iconType: <ScreenSmall />,
};

export const ONLINE_HELP_CENTER = {
    heading: gettext('Online Help Center'),
    description: gettext(
        'Access product "how to\'s" and answers to frequently asked questions in our Help Center.',
    ),
    iconType: <LifePreserver />,
};

export const TRAINING_AND_SUPPORT_A = {
    heading: gettext('Training and support'),
    description: gettext(
        'Get the training and support you need with customized onboarding, Phone support M-F (business hours).',
    ),
    iconType: <Headphone />,
};

export const TRAINING_AND_SUPPORT_B = {
    heading: gettext('Product training'),
    description: gettext(
        'Save time learning the ins and outs of Eventbrite, and ensure you maximize the impact of the platform on your goals.',
    ),
    iconType: <Book />,
};

export const CUSTOMER_SUCCESS_MANGER_SUPPORT = {
    heading: gettext('Customer Success Manager'),
    description: gettext(
        'Get ongoing, personalized support to help you use Eventbrite in the way that best achieves your goals.',
    ),
    iconType: <Headphone />,
};

export const ONSITE_SUPPORT = {
    heading: gettext('On-site staffing and support'),
    description: gettext(
        'Improve your day-of operations with pre-event consultation, on-site technical support, and even full event staffing.',
    ),
    iconType: <Briefcase />,
};

export const LIVE_CHAT_24_7_SUPPORT = {
    heading: gettext('24/7 chat support'),
    description: gettext('24/7 chat support'),
    iconType: <Headphone />,
};
// Currently removed due to Covid-19
export const GENERAL_SUPPORT = {
    heading: gettext('Chat support for event creators with paid events'),
    description: gettext(
        'Get questions answered quickly by talking to a human during business hours.',
    ),
    iconType: <Headphone />,
};
// Currently removed due to Covid-19
export const GENERAL_SUPPORT_HK_SG_NZ = {
    heading: gettext('Chat support for event creators with paid events'),
    description: gettext(
        'Get questions answered quickly by talking to a human during business hours.',
    ),
    iconType: <Headphone />,
};

export const SERVICES_AND_STAFFING = {
    heading: gettext('Services and staffing'),
    description: gettext(
        'Tap into on-site operations support, box office and gate staffing, and custom design services to knock your event out of the park.',
    ),
    iconType: <Friends />,
};

export const RELIABLE_ONSALES = {
    heading: gettext('Reliable onsales'),
    description: gettext(
        'Rely on our 99.99% site uptime to ensure your fans can get tickets during high-demand onsales.',
    ),
    iconType: <Gear />,
};

export const TEAM_PERMISSIONS_A = {
    heading: gettext('Set team access levels'),
    description: gettext(
        'Set your team up with the right permissions, so everyone is equipped to play their role.',
    ),
    iconType: <User />,
};

export const TEAM_PERMISSIONS_B = {
    heading: gettext('Team access and permissions'),
    description: gettext(
        'Work more efficiently across your team, while keeping the right information in the right hands.',
    ),
    iconType: <Friends />,
};

export const WHITE_LABELING_A = {
    heading: gettext('Put your brand front and center'),
    description: gettext(
        'Sell on your website or on new custom community pages to show attendees a fully branded experience.',
    ),
    iconType: <Photo />,
};

export const WHITE_LABELING_B = {
    heading: gettext('Branded community pages'),
    description: gettext(
        'Put your brand front and center with a community landing page and custom branded event templates.',
    ),
    iconType: <Photo />,
};

export const RENT_EQUIPMENT = {
    heading: gettext('Rental equipment for box office and entry'),
    description: gettext(
        'Shorten lines with battle-tested, enterprise-grade tech at your box office, registration table, and gates.',
    ),
    iconType: <CardReader />,
};

export const INCLUDE_ESSENTIALS = {
    heading: gettext('Everything in Essentials'),
    description: gettext(
        'Nail the basics with a 100% mobile-optimized event page, a mobile app, and distribution on partner sites.',
    ),
    iconType: <Download />,
};

export const INCLUDE_ESSENTIALS_WITH_EPP = {
    ...INCLUDE_ESSENTIALS,
    description: gettext(
        'Nail the basics with a 100% mobile-optimised event page, trusted payment processing, a mobile app, and distribution on partner sites',
    ),
};

export const INCLUDE_PROFESSIONAL = {
    heading: gettext('Everything in Professional'),
    description: gettext(
        'Grow your event with a flexible, mobile-optimized event page, an integration to sell on your website, and detailed analytics.',
    ),
    iconType: <Download />,
};

export const PACKAGE_ONE_FEATURES = [
    gettext('One ticket type (ex. "General Admission")'),
    gettext('Ticketing & registration essentials'),
    gettext('Organizer mobile app'),
    gettext('Listing on Eventbrite and partner sites'),
];

export const PACKAGE_ONE_FEATURES_AU_V2 = [
    gettext('Unlimited ticket types (e.g. "General Admission")'),
    gettext('Ticketing & registration essentials'),
    gettext('Organiser mobile app'),
    gettext('Listing on Eventbrite and partner sites'),
];

export const PACKAGE_ONE_FEATURES_IE_UK = [
    gettext('Ticket fees include payment processing'),
    gettext('Sell tickets directly through Facebook'),
    gettext('One ticket type (e.g. "General Admission")'),
    gettext('Mobile app for organisers'),
    gettext('Listing on Eventbrite and partner sites'),
    gettext('Access to a range of partner apps (e.g. Mailchimp)'),
];

export const PACKAGE_TWO_FEATURES_IE_UK = [
    gettext('Everything in Essentials'),
    gettext('Embedded checkout on your website'),
    gettext('Unlimited ticket types'),
    gettext('Detailed sales analytics and reporting'),
    gettext('Customisable checkout forms'),
    gettext('Branded community pages'),
    gettext('Create seating maps'),
];

export const PACKAGE_THREE_FEATURES_IE_UK = [
    gettext('Everything in Professional, plus:'),
    gettext('Team access and permissions'),
    gettext(
        'Access to preferred partners of rental equipment for box office and entry',
    ),
    gettext('Branded community pages'),
    gettext('Product training'),
];

export const PACKAGE_TWO_FEATURES = [
    gettext('Everything in Essentials, plus:'),
    gettext('Unlimited ticket types'),
    gettext('Ticket sales on your own site'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
    gettext('Reserved seating'),
];

export const PACKAGE_TWO_FEATURES_AU = [
    gettext('Everything in Essentials, plus:'),
    gettext('Ticket sales on Facebook'),
    gettext('Ticket sales on your own site'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
    gettext('Reserved seating'),
];

export const PACKAGE_TWO_FEATURES_FOR_CL_CO_PE = [
    gettext('Everything in Essentials, plus:'),
    gettext('Unlimited ticket types'),
    gettext('Ticket sales on your own site'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
];

export const PACKAGE_TWO_FEATURES_FOR_DK_NO_SE = [
    gettext('Everything in Essentials, plus:'),
    gettext('Unlimited ticket types'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
    //  gettext('Payouts before your event'),
];

export const PACKAGE_TWO_FEATURES_ALL = [
    gettext('Everything in Essentials, plus:'),
    gettext('Unlimited ticket types'),
    gettext('Ticket sales on your own site'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
    //  gettext('Payouts before your event'),
    gettext('Reserved seating'),
    gettext('Team access and permissions'),
    gettext('Scheduled payouts'),
];

export const PACKAGE_TWO_FEATURES_CH = [
    gettext('Everything in Essentials, plus:'),
    gettext('Unlimited ticket types'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
    //  gettext('Payouts before your event'),
];

export const PACKAGE_ONE_FEATURES_MARKETPLACE_B = [
    gettext('One ticket type (ex. "General Admission")'),
    gettext('Ticketing & registration essentials'),
    gettext('Organizer mobile app'),
];

export const PACKAGE_TWO_FEATURES_MARKETPLACE_B = [
    gettext('Everything in Essentials, plus:'),
    gettext('Unlimited ticket types'),
    gettext('Public listing on Eventbrite'),
    gettext('Distribution to partner sites'),
    gettext('Ticket sales on your own site'),
    gettext('Customizable checkout forms'),
    gettext('Detailed sales analytics'),
    gettext('Reserved seating'),
];

export const PACKAGE_ONE_GERMAN_FEATURES = [
    gettext('Includes payment transaction for every payment method'),
    gettext('Sell tickets directly through Facebook'),
    gettext('One ticket type (e.g. “Early bird”)'),
    gettext('Mobile app for organizers'),
    gettext('Listing on Eventbrite and partner sites'),
    gettext('Access to a range of partner apps'),
];

export const PACKAGE_TWO_GERMAN_FEATURES = [
    gettext('Everything in Essentials'),
    gettext('Embedded checkout on your website'),
    gettext('Unlimited ticket types'),
    gettext('Detailed sales analytics'),
    gettext('Customisable checkout forms'),
    //  gettext('Payouts before your event'),
    gettext('Reserved seating'),
];

export const PACKAGE_THREE_GERMAN_FEATURES = [
    gettext('Everything in Professional'),
    gettext('Choose your service level'),
    gettext('Product training'),
    gettext('Rental equipment for box office and entry'),
    gettext('Personal Account Manager'),
    gettext('On-site support'),
    gettext('Customized payouts'),
];

export const EVERYTHING_IN_PROFESSIONAL = gettext(
    'Everything in Professional, plus:',
);
export const TEAM_ACCESS = gettext('Team access and permissions');
export const RENTAL_EQUIPMENT = gettext(
    'Rental equipment for box office and entry',
);
export const ON_SITE_STAFFING = gettext('On-site staffing and support');
export const BRAND_PAGES = gettext('Branded community pages');
export const INSTALLMENT = gettext('Installment payments');
export const PRODUCT_TRAINING = gettext('Product training');
export const CUSTOMER_SUCCESS_MANGER = gettext('Customer Success Manager');
export const CONSULTATION_EVENTS_TEAM = gettext(
    "A consultation with our events team to optimize your business's events and pricing strategy.",
);
export const TICKET_FEES_PAYMENT_PROCESSING = gettext(
    'Ticket fees include payment processing',
);

export const PACKAGE_THREE_FEATURES = [
    EVERYTHING_IN_PROFESSIONAL,
    TEAM_ACCESS,
    RENTAL_EQUIPMENT,
    BRAND_PAGES,
    PRODUCT_TRAINING,
];

export const PACKAGE_THREE_FEATURES_CH = [
    EVERYTHING_IN_PROFESSIONAL,
    TEAM_ACCESS,
    BRAND_PAGES,
    PRODUCT_TRAINING,
];

export const PACKAGE_THREE_FEATURES_US = [
    EVERYTHING_IN_PROFESSIONAL,
    CONSULTATION_EVENTS_TEAM,
];

export const CUSTOMER_SUPPORT = {
    featureHeading: gettext(
        'Customer Support phone, chat, and email support for all events',
    ),
    iconType: <Chat />,
};
